/**
 * PaymentProviderConfigPage — 系统设置 / 集成 / 框架通道配置与回调登记页（ADM-PAY-S012，设计稿 PayerMax A1–A6）。
 *
 * 路由 `/settings/integrations/payment-providers/:channel`，`:channel` 只认已登记框架通道（当前 `PAYERMAX`）。
 * 读：能力声明（§10.0）+ 配置列表（§10.1）+ App 列表；写：配置 PUT / 测试 / 启停、endpoint 登记 / 启停 / 退役、
 * 验签材料写入（§10.1–§10.2），全部带 UUID v7 `Idempotency-Key`。
 *
 * 心智：凭据按 App 配，回调按商户账户组（merchant_account_id + mode）配 —— 页面按组展示，组头放回调地址，
 * 组内列 App 配置行。状态全部从响应字段派生，不编造：
 *   - 配置列表读取失败 → 整页 fail-closed（错误 + 重试），不渲染成「未配置」、不列未配置候选；
 *   - 能力声明读取失败 → 列表照常展示，新增 / 编辑禁用（凭据字段集合与本环境 mode 未知）；
 *   - operator / viewer 只读：写控件禁用并写明原因，回调地址 / 商户号 / endpoint_id 仍可复制。
 *
 * 幂等：登记 / 退役回调这类新建或终态动作，结果未知后的重试复用同一枚键（服务端回放上次结果）；测试与启停每次取新键
 * （见 `useIntentKeys`）。
 */

import {
  type PaymentProviderChannel,
  PaymentProviderChannelSchema,
  type PaymentProviderWebhookEndpointSummary,
} from '@novel-admin/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminApiError } from '~/api';
import {
  paymentProviderQueryKeys,
  usePaymentProviderCapability,
  usePaymentProviderConfigs,
} from '~/data/payment-provider-config-queries';
import { type AppRow, useApps } from '~/data/queries';
import {
  type PaymentProviderEndpointAction,
  paymentProviderConfigApi,
} from '~/hooks/use-payment-provider-config';
import { Icons } from '~/lib/icons';
import { newIdempotencyKeyV7 } from '~/lib/idempotency-key';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { ConfirmDialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';
import {
  type EndpointPendingAction,
  MerchantGroupCard,
  type MerchantGroupResult,
} from './MerchantGroupCard';
import type { RowPendingAction } from './ProviderAppConfigRow';
import { type ConfigDrawerTarget, ProviderConfigDrawer } from './ProviderConfigDrawer';
import { VerificationMaterialDialog } from './VerificationMaterialDialog';
import {
  type MerchantGroup,
  SETUP_STEPS,
  groupConfigsByMerchant,
  merchantGroupKey,
  providerPlatformOf,
  shortEndpointId,
  unconfiguredApps,
} from './payment-provider-config-view';
import {
  type ProviderErrorScene,
  type ProviderNotice,
  describeProviderError,
  noticeToastText,
  noticeToastTone,
} from './payment-provider-error-notice';

const CHANNEL_DISPLAY_NAME: Record<PaymentProviderChannel, string> = { PAYERMAX: 'PayerMax' };
const WRITE_ROLES = new Set(['root', 'admin']);
const READONLY_REASON = '需要 admin 权限并完成 MFA';
const CAPABILITY_MISSING_REASON = '能力声明未读到：凭据字段集合与本环境 mode 未知';

/**
 * 用户意图 → 幂等键，按动作性质分两类：
 *   - 新建 / 终态动作（登记回调、退役回调）：同一 scope 在成功前复用同一枚键。结果未知后重试，服务端已执行则回放
 *     上次结果（不会多登记一个回调地址），未执行则照常执行；成功后丢弃，下一次同类动作是新意图。
 *     取键时带上「依据」（登记 = 本页见过的该组全部回调地址，只增不减）：依据变了说明上次意图已在服务端落地
 *     （刷新看到了新地址），再点即新意图、换新键，避免把旧地址的快照回放给新的登记。依据用累积集合而非当前列表：
 *     新地址出现后又被退役，当前列表会复原，累积集合不会。
 *   - 状态切换与只读探针（测试、启用 / 停用配置、启用 / 停用回调）：每次点击一枚新键。重复执行与首次等价，
 *     而复用旧键会在「结果未知 → 他人反向操作 → 本页再操作」时把旧快照回放给新意图。
 */
function useIntentKeys() {
  const pinned = useRef(new Map<string, { key: string; basis: string }>());
  return useMemo(
    () => ({
      pinned(scope: string, basis = ''): string {
        const existing = pinned.current.get(scope);
        if (existing && existing.basis === basis) return existing.key;
        const key = newIdempotencyKeyV7();
        pinned.current.set(scope, { key, basis });
        return key;
      },
      fresh(): string {
        return newIdempotencyKeyV7();
      },
      drop(scope: string) {
        pinned.current.delete(scope);
      },
    }),
    [],
  );
}

type MaterialTarget = { groupKey: string; endpoint: PaymentProviderWebhookEndpointSummary };
type RetireTarget = { group: MerchantGroup; endpoint: PaymentProviderWebhookEndpointSummary };

export default function PaymentProviderConfigPage() {
  const params = useParams<{ channel: string }>();
  const parsed = PaymentProviderChannelSchema.safeParse(params.channel);
  const channel = parsed.success ? parsed.data : null;

  if (!channel) {
    return (
      <div className="card" role="alert" style={{ padding: 22 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>未登记的支付通道</div>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
          本页只服务已登记的框架通道（PayerMax）。Stripe 与 IAP 在集成页各自的入口配置。
        </div>
        <Link className="btn btn-sm" to="/settings/integrations" style={{ marginTop: 12 }}>
          返回集成
        </Link>
      </div>
    );
  }
  return <ChannelConfigPage channel={channel} />;
}

function ChannelConfigPage({ channel }: { channel: PaymentProviderChannel }) {
  const displayName = CHANNEL_DISPLAY_NAME[channel];
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = !!role && WRITE_ROLES.has(role);
  const qc = useQueryClient();
  const capability = usePaymentProviderCapability(channel);
  const configsQuery = usePaymentProviderConfigs(channel);
  const appsQuery = useApps();

  const [drawer, setDrawer] = useState<ConfigDrawerTarget | null>(null);
  const [materialTarget, setMaterialTarget] = useState<MaterialTarget | null>(null);
  const [retireTarget, setRetireTarget] = useState<RetireTarget | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  const [rowPending, setRowPending] = useState<Record<string, RowPendingAction>>({});
  const [rowNotices, setRowNotices] = useState<Record<string, ProviderNotice>>({});
  const [endpointPending, setEndpointPending] = useState<Record<string, EndpointPendingAction>>({});
  const [registerPending, setRegisterPending] = useState<Record<string, boolean>>({});
  const [groupNotices, setGroupNotices] = useState<Record<string, ProviderNotice>>({});
  const [groupResults, setGroupResults] = useState<Record<string, MerchantGroupResult>>({});
  const [knownAllowedHosts, setKnownAllowedHosts] = useState<Record<string, string[]>>({});

  const configs = useMemo(() => configsQuery.data ?? [], [configsQuery.data]);
  const intentKeys = useIntentKeys();
  // 每个商户账户组在本页见过的回调地址（只增不减），作登记幂等键的依据
  const seenEndpointIds = useRef(new Map<string, Set<string>>());
  useEffect(() => {
    for (const config of configs) {
      const key = merchantGroupKey(config.merchant_account_id, config.mode);
      const seen = seenEndpointIds.current.get(key) ?? new Set<string>();
      for (const endpoint of config.webhook_endpoints) seen.add(endpoint.endpoint_id);
      seenEndpointIds.current.set(key, seen);
    }
  }, [configs]);
  const [deactivateEndpointTarget, setDeactivateEndpointTarget] = useState<RetireTarget | null>(
    null,
  );
  const apps = appsQuery.data;
  const appsById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const groups = useMemo(() => groupConfigsByMerchant(configs), [configs]);
  const candidates = useMemo(
    () => (capability.data ? unconfiguredApps(apps, configs, capability.data.platforms) : []),
    [apps, configs, capability.data],
  );
  const appName = useCallback((id: string) => appsById.get(id)?.name ?? id, [appsById]);

  const writeBlockedReason = canWrite ? null : READONLY_REASON;
  // App 列表未就绪时无法判断平台（原生回跳是否必填），编辑一并禁用，避免按「非原生」提交被拒
  const editBlockedReason = !canWrite
    ? READONLY_REASON
    : !capability.data
      ? CAPABILITY_MISSING_REASON
      : appsQuery.isError
        ? 'App 列表读取失败，无法判断 App 平台'
        : !appsQuery.isSuccess
          ? '正在读取 App 列表'
          : null;

  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: paymentProviderQueryKeys.configs(channel) }),
    [qc, channel],
  );

  const reportError = (
    error: unknown,
    scene: ProviderErrorScene,
    scope: string | null,
    inline: (notice: ProviderNotice) => void,
  ) => {
    const notice = describeProviderError(error, scene, {
      deploymentMode: capability.data?.deployment_mode,
      appName,
    });
    if (notice.rotateIdempotencyKey && scope) intentKeys.drop(scope);
    if (notice.refresh) void refresh();
    if (notice.placement === 'inline') inline(notice);
    else toast(noticeToastText(notice), { tone: noticeToastTone(notice) });
  };

  const configById = (appId: string) => configs.find((c) => c.app_id === appId);
  const dropGroupNotice = (groupKey: string) =>
    setGroupNotices((n) => {
      const { [groupKey]: _dropped, ...rest } = n;
      return rest;
    });
  const dropRowNotice = (appId: string) =>
    setRowNotices((n) => {
      const { [appId]: _dropped, ...rest } = n;
      return rest;
    });

  // ---------- App 配置行：测试 / 启用 / 停用 ----------
  const runConfigAction = async (appId: string, action: 'test' | 'activate' | 'deactivate') => {
    const config = configById(appId);
    if (!config || rowPending[appId]) return;
    const key = intentKeys.fresh();
    setRowPending((p) => ({ ...p, [appId]: action }));
    setRowNotices((n) => {
      const { [appId]: _dropped, ...rest } = n;
      return rest;
    });
    try {
      if (action === 'test') {
        await paymentProviderConfigApi.testConnection(channel, appId, key);
        toast('连接正常，可以启用：不代表商户合约、支付方式开通或商品价格已经就绪', {
          tone: 'pos',
        });
      } else {
        await paymentProviderConfigApi.changeConfigState(channel, appId, action, key);
        toast(
          action === 'activate'
            ? `已启用 ${appName(appId)} 的 ${displayName}`
            : `已停用 ${appName(appId)} 的 ${displayName}：新的支付立即不可用`,
          { tone: 'pos' },
        );
      }
      void refresh();
    } catch (error) {
      reportError(error, action, null, (notice) =>
        setRowNotices((n) => ({ ...n, [appId]: notice })),
      );
    } finally {
      setRowPending((p) => ({ ...p, [appId]: null }));
    }
  };

  // ---------- 商户账户组：登记回调 ----------
  const registerEndpoint = async (group: MerchantGroup) => {
    if (registerPending[group.key]) return;
    const scope = `register|${group.key}`;
    const seen = new Set(seenEndpointIds.current.get(group.key));
    for (const endpoint of group.endpoints) seen.add(endpoint.endpoint_id);
    const key = intentKeys.pinned(scope, [...seen].sort().join(','));
    setRegisterPending((p) => ({ ...p, [group.key]: true }));
    dropGroupNotice(group.key);
    try {
      const result = await paymentProviderConfigApi.registerEndpoint(
        channel,
        { merchant_account_id: group.merchantAccountId, mode: group.mode },
        key,
      );
      intentKeys.drop(scope);
      setGroupResults((r) => ({
        ...r,
        [group.key]: {
          kind: 'registered',
          endpointId: result.endpoint_id,
          callbackUrl: result.callback_url,
        },
      }));
      void refresh();
    } catch (error) {
      reportError(error, 'register', scope, (notice) =>
        setGroupNotices((n) => ({ ...n, [group.key]: notice })),
      );
    } finally {
      setRegisterPending((p) => ({ ...p, [group.key]: false }));
    }
  };

  // ---------- endpoint：停用 / 恢复 / 退役 ----------
  const changeEndpoint = async (
    group: MerchantGroup,
    endpoint: PaymentProviderWebhookEndpointSummary,
    action: PaymentProviderEndpointAction,
  ) => {
    if (endpointPending[endpoint.endpoint_id]) return;
    // 退役是终态（同新建）：结果未知后重试复用同一枚键；启停每次新键
    const scope = action === 'retire' ? `retire|${endpoint.endpoint_id}` : null;
    const key = scope ? intentKeys.pinned(scope) : intentKeys.fresh();
    setEndpointPending((p) => ({ ...p, [endpoint.endpoint_id]: action }));
    dropGroupNotice(group.key);
    try {
      await paymentProviderConfigApi.changeEndpointState(
        channel,
        endpoint.endpoint_id,
        action,
        key,
      );
      if (scope) intentKeys.drop(scope);
      toast(
        action === 'retire'
          ? `回调地址 ${shortEndpointId(endpoint.endpoint_id)} 已退役`
          : action === 'deactivate'
            ? `回调地址 ${shortEndpointId(endpoint.endpoint_id)} 已停用`
            : `回调地址 ${shortEndpointId(endpoint.endpoint_id)} 已恢复`,
        { tone: 'pos' },
      );
      void refresh();
    } catch (error) {
      reportError(error, 'endpoint-state', scope, (notice) =>
        setGroupNotices((n) => ({ ...n, [group.key]: notice })),
      );
    } finally {
      setEndpointPending((p) => ({ ...p, [endpoint.endpoint_id]: null }));
    }
  };

  const materialGroup = materialTarget
    ? groups.find((g) => g.key === materialTarget.groupKey)
    : undefined;
  const openRotateForGroup = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    const endpoint =
      group?.endpoints.find((e) => e.is_active && e.verifying_material_count > 0) ??
      group?.endpoints.find((e) => e.verifying_material_count > 0);
    if (!group || !endpoint) return;
    setDrawer(null);
    setMaterialTarget({ groupKey, endpoint });
  };

  const editingConfig = drawer?.mode === 'edit' ? configById(drawer.appId) : undefined;
  const deactivateConfig = deactivateTarget ? configById(deactivateTarget) : undefined;

  // ---------- 页面骨架 ----------
  const capabilityData = capability.data;
  const newBlockedReason = !canWrite
    ? READONLY_REASON
    : configsQuery.isError
      ? '配置列表未读到，无法判断是否已配置'
      : !capabilityData
        ? capability.isError
          ? '凭据字段未知'
          : '正在读取能力声明'
        : appsQuery.isError
          ? 'App 列表读取失败'
          : candidates.length === 0
            ? '没有平台适用且未配置的 App'
            : null;
  const openNew = (appId?: string) => setDrawer({ mode: 'new', appId });

  return (
    <>
      <div className="hstack" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Link
            to="/settings/integrations"
            style={{
              fontSize: 11.5,
              color: 'var(--fg-muted)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icons.IconChevLeft size={11} />
            集成
          </Link>
          <h2
            style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            {displayName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
            框架通道 · 凭据按 App 配置，回调按商户账户配置。同一商户号 + mode 的 App
            共用回调地址与平台公钥。
          </div>
        </div>
        <button
          type="button"
          className={newBlockedReason ? 'btn' : 'btn btn-accent'}
          disabled={newBlockedReason !== null}
          title={newBlockedReason ?? undefined}
          onClick={() => openNew()}
        >
          <Icons.IconPlus size={13} />
          新增 App 配置
        </button>
      </div>

      {!canWrite && (
        <div className="card-flat hstack" style={{ padding: '10px 12px', gap: 10 }}>
          <Icons.IconLock size={14} style={{ color: 'var(--fg-muted)' }} />
          <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
            <span style={{ color: 'var(--fg)', fontWeight: 500 }}>只读</span> · 你的角色是{' '}
            {role ?? '未知'}
            。保存、测试、启停、登记回调与写入验签材料需要 root / admin 并完成
            MFA。回调地址可以复制给有权限的同事处理。
          </div>
        </div>
      )}

      {capabilityData ? (
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
          {capabilityData.deployment_mode ? (
            <span className="chip chip-info">
              本环境 mode{' '}
              <span className="mono" style={{ fontWeight: 600 }}>
                {capabilityData.deployment_mode}
              </span>
            </span>
          ) : (
            <span className="chip chip-warn">本环境未声明 mode · 保存必被拒，请联系运维</span>
          )}
          <span className="chip">
            适用平台 <span className="mono">{capabilityData.platforms.join(' · ')}</span>
          </span>
          <span className="chip">
            凭据字段 <span className="mono">{capabilityData.credential_fields.length}</span> 项 ·
            只写不回显
          </span>
          {capabilityData.currency_rule === 'SINGLE_PRICE_ROW' && (
            <span className="chip">原生价格 · 单币种</span>
          )}
        </div>
      ) : capability.isError ? (
        <div className="card" role="alert" style={{ padding: 22, display: 'flex', gap: 14 }}>
          <StateIcon tone="warn" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-strong)' }}>
              能力声明读取失败
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6, marginTop: 4 }}>
              配置列表照常展示，但凭据字段集合与本环境 mode 未知，新增与编辑暂不可用。
            </div>
            <div
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 6 }}
            >
              GET …/payment-providers/{channel}/capability
              {errorCodeOf(capability.error)}
            </div>
            <div className="hstack" style={{ gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void capability.refetch()}
              >
                重试
              </button>
              <button type="button" className="btn btn-sm" disabled title="凭据字段未知">
                新增 App 配置
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {configsQuery.isPending ? (
        <ConfigsSkeleton />
      ) : configsQuery.isError ? (
        <div className="card" role="alert" style={{ padding: 22, display: 'flex', gap: 14 }}>
          <StateIcon tone="neg" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-strong)' }}>
              {displayName} 配置读取失败
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6, marginTop: 4 }}>
              没能读取配置列表，当前无法判断哪些 App 已配置。请重试；多次失败请联系运维。
            </div>
            <div
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 6 }}
            >
              GET …/payment-providers/{channel}/configs
              {errorCodeOf(configsQuery.error)}
            </div>
            <div className="hstack" style={{ gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void configsQuery.refetch()}
              >
                <Icons.IconRefresh size={12} />
                重试
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled
                title="配置列表未读到，无法判断是否已配置"
              >
                新增 App 配置
              </button>
            </div>
          </div>
        </div>
      ) : configs.length === 0 ? (
        <EmptyState
          candidates={candidates}
          platforms={capabilityData?.platforms ?? []}
          newBlockedReason={newBlockedReason}
          onNew={() => openNew()}
        />
      ) : (
        <>
          <div className="card-flat" style={{ padding: 14 }}>
            <div className="hstack" style={{ gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>接入流程</span>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                每个 App 行标出当前停在哪一步
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 10,
              }}
            >
              {SETUP_STEPS.map((step, index) => (
                <div key={step.title} className="vstack" style={{ gap: 4 }}>
                  <div className="hstack" style={{ gap: 6 }}>
                    <span
                      className="mono"
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        background: 'var(--bg-muted)',
                        color: 'var(--fg-muted)',
                        fontSize: 10.5,
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{step.title}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                    {step.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {groups.map((group) => (
            <MerchantGroupCard
              key={group.key}
              group={group}
              appsById={appsById}
              writeBlockedReason={writeBlockedReason}
              editBlockedReason={editBlockedReason}
              rowPending={rowPending}
              rowNotices={rowNotices}
              endpointPending={endpointPending}
              registerPending={!!registerPending[group.key]}
              groupNotice={groupNotices[group.key] ?? null}
              result={groupResults[group.key] ?? null}
              onRegister={() => void registerEndpoint(group)}
              onWriteMaterial={(endpoint) => setMaterialTarget({ groupKey: group.key, endpoint })}
              onEndpointState={(endpoint, action) =>
                // 停用后组内不再有「启用且有材料」的回调地址 → 整组回调被拒，先确认
                action === 'deactivate' && leavesGroupWithoutUsableEndpoint(group, endpoint)
                  ? setDeactivateEndpointTarget({ group, endpoint })
                  : void changeEndpoint(group, endpoint, action)
              }
              onRetire={(endpoint) => setRetireTarget({ group, endpoint })}
              onDismissResult={() =>
                setGroupResults((r) => {
                  const { [group.key]: _dropped, ...rest } = r;
                  return rest;
                })
              }
              onDismissGroupNotice={() =>
                setGroupNotices((n) => {
                  const { [group.key]: _dropped, ...rest } = n;
                  return rest;
                })
              }
              onTest={(appId) => void runConfigAction(appId, 'test')}
              onActivate={(appId) => void runConfigAction(appId, 'activate')}
              onDeactivate={(appId) => setDeactivateTarget(appId)}
              onEdit={(appId) => setDrawer({ mode: 'edit', appId })}
              onReplaceCredential={(appId) =>
                setDrawer({ mode: 'edit', appId, replaceCredential: true })
              }
              onDismissRowNotice={(appId) =>
                setRowNotices((n) => {
                  const { [appId]: _dropped, ...rest } = n;
                  return rest;
                })
              }
            />
          ))}

          <UnconfiguredAppsSection
            candidates={candidates}
            platforms={capabilityData?.platforms ?? []}
            capabilityReady={!!capabilityData}
            appsLoading={appsQuery.isLoading}
            appsError={appsQuery.isError}
            onRetryApps={appsQuery.refetch}
            addBlockedReason={editBlockedReason}
            onNew={openNew}
          />
        </>
      )}

      {drawer && capabilityData && (drawer.mode === 'new' || editingConfig) && (
        <ProviderConfigDrawer
          key={drawer.mode === 'edit' ? `edit:${drawer.appId}` : 'new'}
          channel={channel}
          capability={capabilityData}
          target={drawer}
          existing={editingConfig}
          configs={configs}
          apps={apps}
          candidates={candidates}
          knownAllowedHosts={knownAllowedHosts}
          onAllowedHosts={(appId, hosts) => setKnownAllowedHosts((h) => ({ ...h, [appId]: hosts }))}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            // 保存即补正：清掉该行上一次测试 / 启用留下的一次性提示
            if (drawer.mode === 'edit') dropRowNotice(drawer.appId);
            setDrawer(null);
            void refresh();
          }}
          onRefresh={() => void refresh()}
          onOpenRotate={openRotateForGroup}
        />
      )}

      {materialTarget && materialGroup && (
        <VerificationMaterialDialog
          channel={channel}
          group={materialGroup}
          endpoint={materialTarget.endpoint}
          appsById={appsById}
          editBlockedReason={editBlockedReason}
          onClose={() => setMaterialTarget(null)}
          onRefresh={() => void refresh()}
          onDone={(result, rotated) => {
            setMaterialTarget(null);
            setGroupResults((r) => ({
              ...r,
              [materialGroup.key]: {
                kind: 'material',
                rotated,
                verifyingCount: result.verifying_material_count,
                retireAfter: result.latest_retiring_retire_after,
              },
            }));
            // 材料已写入：组内「缺验签材料」的行内提示与组级失败提示随之失效
            dropGroupNotice(materialGroup.key);
            for (const config of materialGroup.configs) {
              if (rowNotices[config.app_id]?.kind === 'material-missing')
                dropRowNotice(config.app_id);
            }
            void refresh();
          }}
          onEditApp={(appId) => {
            setMaterialTarget(null);
            setDrawer({ mode: 'edit', appId, replaceCredential: true });
          }}
        />
      )}

      {retireTarget && (
        <RetireEndpointConfirm
          target={retireTarget}
          appName={appName}
          onConfirm={() => void changeEndpoint(retireTarget.group, retireTarget.endpoint, 'retire')}
          onClose={() => setRetireTarget(null)}
        />
      )}

      {deactivateEndpointTarget && (
        <ConfirmDialog
          tone="warn"
          title={`停用回调地址 ${shortEndpointId(deactivateEndpointTarget.endpoint.endpoint_id)}`}
          message={
            <>
              停用后，商户组{' '}
              <span className="mono">
                {deactivateEndpointTarget.group.merchantAccountId} ·{' '}
                {deactivateEndpointTarget.group.mode}
              </span>{' '}
              不再有启用且有验签材料的回调地址，<b>该组所有回调都会被拒</b>
              ，退款和争议通知不再自动处理。可随时恢复。
            </>
          }
          details={[
            `影响 App：${deactivateEndpointTarget.group.configs.map((c) => appName(c.app_id)).join('、')}`,
          ]}
          confirmLabel="仍要停用"
          onConfirm={() =>
            void changeEndpoint(
              deactivateEndpointTarget.group,
              deactivateEndpointTarget.endpoint,
              'deactivate',
            )
          }
          onClose={() => setDeactivateEndpointTarget(null)}
        />
      )}

      {deactivateTarget && deactivateConfig && (
        <ConfirmDialog
          tone="danger"
          title={`停用 ${appName(deactivateTarget)} 的 ${displayName}`}
          message={`停用后，${appName(deactivateTarget)} 上新的 ${displayName} 支付立即不可用。进行中的订单和回调不受影响。`}
          confirmLabel="确认停用"
          onConfirm={() => void runConfigAction(deactivateTarget, 'deactivate')}
          onClose={() => setDeactivateTarget(null)}
        />
      )}
    </>
  );
}

function StateIcon({ tone }: { tone: 'neg' | 'warn' }) {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        background: tone === 'neg' ? 'var(--neg-soft)' : 'var(--warn-soft)',
        color: tone === 'neg' ? 'var(--neg)' : 'var(--warn)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icons.IconAlert size={16} />
    </div>
  );
}

/** 加载骨架：与真实分组同形（组头 → 回调地址行 → 两个 App 行）。 */
function ConfigsSkeleton() {
  const appRow = (last: boolean) => (
    <div
      style={{ padding: '12px 16px', borderBottom: last ? undefined : '1px solid var(--border)' }}
      className="hstack"
    >
      <span className="sk" style={{ width: 28, height: 28, borderRadius: 6 }} />
      <div className="vstack" style={{ gap: 6, flex: 1 }}>
        <span className="sk" style={{ width: 260, maxWidth: '100%', height: 12 }} />
        <span className="sk" style={{ width: 420, maxWidth: '100%', height: 9 }} />
      </div>
      <span className="sk" style={{ width: 140, height: 18 }} />
      <span className="sk" style={{ width: 150, height: 24 }} />
    </div>
  );
  return (
    <div className="card" style={{ overflow: 'hidden' }} aria-busy="true" aria-label="正在读取配置">
      <div
        className="hstack"
        style={{
          gap: 12,
          padding: '12px 16px',
          background: 'var(--bg-subtle)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span className="sk" style={{ width: 32, height: 32, borderRadius: 7 }} />
        <div className="vstack" style={{ gap: 6, flex: 1 }}>
          <span className="sk" style={{ width: 70, height: 9 }} />
          <span className="sk" style={{ width: 240, height: 14 }} />
        </div>
        <span className="sk" style={{ width: 96, height: 26, borderRadius: 6 }} />
      </div>
      <div
        style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}
        className="hstack"
      >
        <span className="sk" style={{ width: 420, maxWidth: '50%', height: 12 }} />
        <span className="sk" style={{ width: 60, height: 18, borderRadius: 999 }} />
        <div style={{ flex: 1 }} />
        <span className="sk" style={{ width: 220, height: 24 }} />
      </div>
      {appRow(false)}
      {appRow(true)}
    </div>
  );
}

function EmptyState({
  candidates,
  platforms,
  newBlockedReason,
  onNew,
}: {
  candidates: AppRow[];
  platforms: readonly string[];
  newBlockedReason: string | null;
  onNew: () => void;
}) {
  const names = candidates.slice(0, 3).map((a) => a.name);
  return (
    <div
      className="card"
      style={{
        padding: '36px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icons.IconWallet size={20} />
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--fg-strong)' }}>
        尚未配置任何 App
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', maxWidth: 560, lineHeight: 1.6 }}>
        先为一个 App 保存 PayerMax
        配置，再按商户账户登记回调地址、写入平台公钥作为验签材料，测试连接通过后启用。
      </div>
      <div
        className="hstack"
        style={{
          gap: 6,
          fontSize: 11,
          color: 'var(--fg-muted)',
          margin: '4px 0 6px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {SETUP_STEPS.map((step, index) => (
          <span key={step.title} className="hstack" style={{ gap: 6 }}>
            {index > 0 && <span style={{ color: 'var(--fg-faint)' }}>→</span>}
            <span className="chip">
              {index + 1} {step.title}
            </span>
          </span>
        ))}
      </div>
      <button
        type="button"
        className={newBlockedReason ? 'btn' : 'btn btn-accent'}
        disabled={newBlockedReason !== null}
        title={newBlockedReason ?? undefined}
        onClick={onNew}
      >
        <Icons.IconPlus size={13} />
        新增 App 配置
      </button>
      {candidates.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          可配置的 App：{names.join('、')}
          {candidates.length > names.length ? ` 等 ${candidates.length} 个` : ''}（
          {platforms.join(' / ')}）
        </div>
      )}
    </div>
  );
}

function UnconfiguredAppsSection({
  candidates,
  platforms,
  capabilityReady,
  appsLoading,
  appsError,
  onRetryApps,
  addBlockedReason,
  onNew,
}: {
  candidates: AppRow[];
  platforms: readonly string[];
  capabilityReady: boolean;
  appsLoading: boolean;
  appsError: boolean;
  onRetryApps: () => void;
  addBlockedReason: string | null;
  onNew: (appId: string) => void;
}) {
  return (
    <section className="card" style={{ padding: '4px 18px 10px' }} aria-label="未配置的 App">
      <div
        style={{
          padding: '12px 0',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 13.5 }}>
            未配置的 App{' '}
            {capabilityReady && !appsError && (
              <span className="mono" style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>
                {candidates.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
            {capabilityReady
              ? `PayerMax 适用 ${platforms.join(' / ')} · 选择一个 App 新增配置`
              : '能力声明未读到，无法判断哪些平台适用'}
          </div>
        </div>
      </div>
      {!capabilityReady ? null : appsError ? (
        <div
          className="hstack"
          style={{ gap: 8, padding: '10px 0', fontSize: 12, color: 'var(--fg-muted)' }}
        >
          App 列表读取失败，无法列出候选。
          <button type="button" className="btn btn-sm" onClick={onRetryApps}>
            重试
          </button>
        </div>
      ) : appsLoading ? (
        <div style={{ padding: '10px 0' }}>
          <span className="sk" style={{ width: 240, height: 12 }} />
        </div>
      ) : candidates.length === 0 ? (
        <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--fg-muted)' }}>
          所有适用平台的 App 都已配置。
        </div>
      ) : (
        candidates.map((app, index) => (
          <div
            key={app.id}
            className="hstack"
            style={{
              gap: 10,
              padding: '10px 0',
              borderBottom:
                index === candidates.length - 1 ? undefined : '1px dashed var(--border)',
            }}
          >
            <AppIcon app={app} size={{ box: 22, font: 9, radius: 5 }} />
            <span style={{ fontSize: 12.5, fontWeight: 500 }}>{app.name}</span>
            <span className="chip mono" style={{ fontSize: 9.5 }}>
              {providerPlatformOf(app)}
            </span>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn btn-sm"
              disabled={addBlockedReason !== null}
              title={addBlockedReason ?? undefined}
              onClick={() => onNew(app.id)}
            >
              新增配置
            </button>
          </div>
        ))
      )}
    </section>
  );
}

function RetireEndpointConfirm({
  target,
  appName,
  onConfirm,
  onClose,
}: {
  target: RetireTarget;
  appName: (id: string) => string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { group, endpoint } = target;
  // 退役会让该地址的材料转 RETIRED（停用不会）。A8 §10.2 规则 4：同步响应验签取组内未退役（含已停用）
  // endpoint 的材料，回调验签另要求 endpoint 启用 —— 两种后果分开判
  const lastMaterial =
    endpoint.verifying_material_count > 0 &&
    !group.endpoints.some(
      (e) => e.endpoint_id !== endpoint.endpoint_id && e.verifying_material_count > 0,
    );
  const lastUsable = leavesGroupWithoutUsableEndpoint(group, endpoint);
  const remaining = group.endpoints.filter((e) => e.endpoint_id !== endpoint.endpoint_id);
  const groupLabel = (
    <span className="mono">
      {group.merchantAccountId} · {group.mode}
    </span>
  );
  return (
    <ConfirmDialog
      tone="danger"
      title={`退役回调地址 ${shortEndpointId(endpoint.endpoint_id)}`}
      message={
        <>
          退役<b>不可恢复</b>，该地址此后收到的回调一律被拒。
          {(lastMaterial || lastUsable) && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                background: 'var(--neg-soft)',
                borderRadius: 'var(--radius)',
                fontSize: 12,
                color: 'var(--fg)',
              }}
            >
              {lastMaterial ? (
                <>
                  这是商户组 {groupLabel} 最后一个有验签材料的回调地址。退役后该组
                  <b>没有当前有效验签材料</b>
                  ：所有回调都会被拒，新支付发起结果未知，退款、争议与对账的长期读取都无法完成，直到
                  {remaining.length === 0
                    ? '重新登记回调地址并写入材料'
                    : remaining.some((e) => e.is_active)
                      ? '给剩余回调地址写入材料'
                      : '给剩余回调地址写入材料并恢复其中一个'}
                  。
                </>
              ) : (
                <>
                  退役后商户组 {groupLabel} 不再有启用且有验签材料的回调地址，该组
                  <b>所有回调都会被拒</b>
                  ，退款和争议通知不再自动处理。
                </>
              )}
            </div>
          )}
        </>
      }
      details={[
        '确认 PayerMax 后台已改为新地址并收到回调',
        '该组仍有订单需要长期读取（退款、争议、对账、中继重放）期间，至少保留一个未退役且有验签材料的回调地址',
        `影响 App：${group.configs.map((c) => appName(c.app_id)).join('、')}`,
      ]}
      confirmLabel={lastMaterial || lastUsable ? '仍要退役' : '确认退役'}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/**
 * 该回调地址是组内最后一个「启用且有验签材料」的：停用或退役它会让整组回调被拒。
 * 判据与进度第 ③ 步一致；它本身不可用时，组内可用数不会因这次操作减少。
 */
function leavesGroupWithoutUsableEndpoint(
  group: MerchantGroup,
  endpoint: PaymentProviderWebhookEndpointSummary,
): boolean {
  const usable = (e: PaymentProviderWebhookEndpointSummary) =>
    e.is_active && e.verifying_material_count > 0;
  return (
    usable(endpoint) &&
    !group.endpoints.some((e) => e.endpoint_id !== endpoint.endpoint_id && usable(e))
  );
}

/** 读取失败卡上的机读码后缀（` · SERVICE_UNAVAILABLE`）；非 API 错误不显示。 */
function errorCodeOf(error: unknown): string {
  return error instanceof AdminApiError && error.code ? ` · ${error.code}` : '';
}
